import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, Trash2, HelpCircle } from "lucide-react";
import type { ToolType } from "../../../types";

const RESERVED_PIPELINE_IDS = ["default", "sourceproperties", "maintenance", "general", "collections"];

const FIXED_NEW_POSITIONS = [
  { id: "new", displayName: "Nuevo" },
  { id: "delivered", displayName: "Entregado" },
  { id: "read", displayName: "Leido" },
  { id: "answered", displayName: "Respondido" },
];

const FIXED_PENDING_POSITIONS = [{ id: "redirected", displayName: "Redireccion humano" }];

const FIXED_SUCCESS_POSITIONS = [{ id: "closed", displayName: "Negocio Cerrado" }];

const FIXED_FAILURE_POSITIONS = [
  { id: "inactive", displayName: "Inactivo" },
  { id: "discarded", displayName: "Descartado" },
];

const STAGE_TOOLTIPS: Record<string, string> = {
  Nuevo: "En esta etapa la conversacion esta siendo manejada por el agente IA",
  Pendiente:
    "La conversacion avanzo de etapa y requiere la atencion y/o accion de un agente humano. Posibles usos de posiciones en esta etapa: Redireccion humano (el agente IA detecta que la solicitud requiere atencion humana), conversaciones que avanzan porque el AI filtro la parte mas alta del embudo (Etapa nuevo), o cuando el agente IA cumplio su objetivo de convencer al usuario de avanzar a una siguiente etapa.",
  Exito:
    "Usuario convertido, se requirio de un agente humano para cerrar un negocio o para dar por finalizada exitosamente una conversacion. En un proceso comercial equivaldria a un negocio cerrado o en estado de tramitacion.",
  Fallido: "Tambien es una etapa final de la conversacion sin exito.",
};

const POSITION_TOOLTIPS: Record<string, string> = {
  new: "Conversacion iniciada, aun sin confirmacion de entrega del primer mensaje al usuario.",
  delivered: "Confirmacion de whatsapp de que el primer mensaje ha sido entregado al usuario.",
  read: "Confirmacion de whatsapp de que el primer mensaje ha sido leido por el usuario.",
  answered:
    "Usuario ha interactuado al menos una vez en la conversacion (o respondio el primer mensaje de la conversacion).",
  redirected:
    "El agente IA detecta que la solicitud del cliente requiere la atencion de un agente humano.",
  closed: "La conversacion resulto en un nuevo negocio.",
  inactive: 'Se cumplio el tiempo de inactividad estando en la etapa "Nuevo".',
  discarded:
    "La conversacion fue descartada de acuerdo a reglas especificas (ej: un prospecto que no le interesa un inmueble en un proceso de comercial).",
};

const ALL_TOOLS = [
  { value: "transferConversation", label: "Transferir Conversacion" },
  { value: "scheduleVisit", label: "Agendar Visita" },
  { value: "getProperty", label: "Obtener Propiedad" },
  { value: "getPropertyByCode", label: "Obtener Propiedad por Codigo" },
  { value: "searchProperties", label: "Buscar Propiedades" },
  { value: "assignAgent", label: "Asignar Agente" },
  { value: "setEmail", label: "Correo electrónico" },
  { value: "registerListingRequest", label: "Registrar Solicitud de Publicacion" },
  { value: "discardLead", label: "Descartar Lead" },
  { value: "calculateDaysFor", label: "Calcular Dias" },
  { value: "createMaintenanceRequest", label: "Registrar Solicitud de Mantenimiento" },
  { value: "getCustomerProperties", label: "Obtener inmuebles del cliente" },
  { value: "getCustomerDocuments", label: "Obtener documentos del cliente" },
  { value: "createServiceRequest", label: "Crear solicitud de servicio" },
  { value: "updateServiceRequest", label: "Actualizar solicitud de servicio" },
  { value: "updatePipelinePosition", label: "Actualizar estado del usuario" },
  { value: "requestRentGuaranteePreApproval", label: "Solicitar estudio de fianza" },
  { value: "setAppointmentIntention", label: "Registrar intencion de cita" },
  { value: "sendMedia", label: "Enviar multimedia" },
] as const;

const HUMAN_ASSIGNMENT_TOOLTIP =
  "La asignacion y notificacion se genera unicamente si el cambio a este estado es generado por el agente IA";

export type CustomAgentFormData = {
  agentName: string;
  pipelineId: string;
  pipelineDisplayName: string;
  pendingPositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  successPositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  failurePositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  systemPrompt: string;
  selectedTools: ToolType[];
};

type Step2CustomConfigurationProps = {
  formData: CustomAgentFormData;
  onFormDataChange: (data: CustomAgentFormData) => void;
};

const Step2CustomConfiguration = ({
  formData,
  onFormDataChange,
}: Step2CustomConfigurationProps) => {
  const addPendingPosition = () => {
    onFormDataChange({
      ...formData,
      pendingPositions: [
        ...formData.pendingPositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removePendingPosition = (index: number) => {
    onFormDataChange({
      ...formData,
      pendingPositions: formData.pendingPositions.filter((_, i) => i !== index),
    });
  };

  const updatePendingPosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.pendingPositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, pendingPositions: next });
  };

  const addSuccessPosition = () => {
    onFormDataChange({
      ...formData,
      successPositions: [
        ...formData.successPositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removeSuccessPosition = (index: number) => {
    onFormDataChange({
      ...formData,
      successPositions: formData.successPositions.filter((_, i) => i !== index),
    });
  };

  const updateSuccessPosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.successPositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, successPositions: next });
  };

  const addFailurePosition = () => {
    onFormDataChange({
      ...formData,
      failurePositions: [
        ...formData.failurePositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removeFailurePosition = (index: number) => {
    onFormDataChange({
      ...formData,
      failurePositions: formData.failurePositions.filter((_, i) => i !== index),
    });
  };

  const updateFailurePosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.failurePositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, failurePositions: next });
  };

  const pipelineIdError =
    formData.pipelineId && RESERVED_PIPELINE_IDS.includes(formData.pipelineId.toLowerCase())
      ? "Este ID esta reservado. Usa otro (ej: mi-proceso)"
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-semibold mb-4">
        Configuracion del Agente Personalizado
      </p>

      {/* Pipeline metadata */}
      <div>
        <p className="text-base font-semibold mb-3">
          Proceso (ciclo de vida de las conversaciones)
        </p>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              ID del proceso (ej: ventas) <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.pipelineId}
              onChange={(e) =>
                onFormDataChange({
                  ...formData,
                  pipelineId: e.target.value.replace(/\s/g, "-").toLowerCase(),
                })
              }
              placeholder="ventas"
              className={pipelineIdError ? "border-red-500" : ""}
            />
            {pipelineIdError && (
              <p className="text-sm text-red-500 mt-1">
                {pipelineIdError}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              Nombre del proceso (para mostrar) <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.pipelineDisplayName}
              onChange={(e) =>
                onFormDataChange({ ...formData, pipelineDisplayName: e.target.value })
              }
              placeholder="Ventas"
            />
          </div>
        </div>
      </div>

      {/* Pipeline view -- one column per stage, positions stacked vertically */}
      <div>
        <p className="text-base font-semibold mb-3">
          Etapas del proceso
        </p>
        <p className="text-sm text-gray-600 mb-3">
          Las etapas pueden tener diferentes posiciones. Puedes anadir posiciones personalizadas
          para las etapas "Pendiente", "Exito" y "Fallido".
        </p>
        <div className="border border-gray-200 rounded-md bg-gray-50 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ minWidth: 0 }}>
            {/* New stage column */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Nuevo
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Nuevo}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_NEW_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>

            {/* Pending stage column -- fixed redirected + editable + add at bottom */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Pendiente
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[320px]">
                      {STAGE_TOOLTIPS.Pendiente}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_PENDING_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {formData.pendingPositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-blue-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: enRevision)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updatePendingPosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: En revision)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updatePendingPosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updatePendingPosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removePendingPosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                onClick={addPendingPosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>

            {/* Success stage column -- fixed + custom + add at bottom */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Exito
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Exito}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_SUCCESS_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {formData.successPositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-green-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: ventaCerrada)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updateSuccessPosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: Venta cerrada)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updateSuccessPosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updateSuccessPosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removeSuccessPosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-green-600 border-green-300 hover:bg-green-50"
                onClick={addSuccessPosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>

            {/* Failed stage column -- fixed + custom + add at bottom */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Fallido
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Fallido}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_FAILURE_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {formData.failurePositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-red-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: noInteresado)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updateFailurePosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: No interesado)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updateFailurePosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updateFailurePosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removeFailurePosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
                onClick={addFailurePosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Agent name */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Nombre del agente <span className="text-red-500">*</span>
        </Label>
        <Input
          value={formData.agentName}
          onChange={(e) => onFormDataChange({ ...formData, agentName: e.target.value })}
          placeholder="Ingresa el nombre del agente"
        />
      </div>

      {/* Prompt */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Instrucciones del agente <span className="text-red-500">*</span>
        </Label>
        <Textarea
          value={formData.systemPrompt}
          onChange={(e) => onFormDataChange({ ...formData, systemPrompt: e.target.value })}
          placeholder="Describe el rol y las instrucciones del agente (ej: responder consultas, redirigir a humano, etc.)"
          className="h-[70vh] min-h-[400px] resize-y overflow-y-auto text-sm font-mono p-4"
        />
      </div>

      {/* Tools */}
      <div>
        <p className="text-base font-semibold mb-4">
          Herramientas
        </p>
        <div className="flex flex-col gap-2">
          {ALL_TOOLS.map((tool) => (
            <div key={tool.value} className="flex items-center gap-2">
              <Checkbox
                checked={formData.selectedTools?.includes(tool.value as ToolType) ?? false}
                onCheckedChange={(checked) => {
                  const currentTools = formData.selectedTools || [];
                  const toolValue = tool.value as ToolType;
                  const newTools = checked
                    ? [...currentTools, toolValue]
                    : currentTools.filter((t) => t !== toolValue);
                  onFormDataChange({ ...formData, selectedTools: newTools });
                }}
              />
              <label className="text-sm">{tool.label}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Step2CustomConfiguration;
